'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface PoolDevice {
  id: string;
  device_id: string;
  disponibile: boolean;
  posizione_magazzino: string;
  note: string;
  device?: { hostname: string; categoria: string; stato: string; modello: string; } | null;
}

interface Prestito {
  id: string;
  device_id: string;
  reparto_origine_id: string | null;
  reparto_destinazione_id: string | null;
  data_prestito: string;
  data_restituzione_prevista: string;
  data_restituzione_effettiva: string | null;
  stato: 'attivo' | 'restituito' | 'scaduto';
  motivo: string;
  note: string;
  device?: { hostname: string; categoria: string; } | null;
  reparto_origine?: { nome: string } | null;
  reparto_destinazione?: { nome: string } | null;
}

interface Device { id: string; hostname: string; categoria: string; }
interface Reparto { id: string; nome: string; }

const STATO_PRESTITO_COLORS: Record<string, string> = {
  attivo: 'bg-sky-500/20 text-sky-400',
  restituito: 'bg-emerald-500/20 text-emerald-400',
  scaduto: 'bg-red-500/20 text-red-400',
};

export default function PoolDispositiviContent() {
  const [activeTab, setActiveTab] = useState<'pool' | 'prestiti'>('pool');
  const [pool, setPool] = useState<PoolDevice[]>([]);
  const [prestiti, setPrestiti] = useState<Prestito[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reparti, setReparti] = useState<Reparto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const { canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, prRes, dRes, rRes] = await Promise.all([
        supabase.from('pool_dispositivi').select('*, device:devices(hostname, categoria, stato, modello)').order('created_at', { ascending: false }),
        supabase.from('prestiti_reparti').select('*, device:devices(hostname, categoria), reparto_origine:reparti!prestiti_reparti_reparto_origine_id_fkey(nome), reparto_destinazione:reparti!prestiti_reparti_reparto_destinazione_id_fkey(nome)').order('data_prestito', { ascending: false }),
        supabase.from('devices').select('id, hostname, categoria').order('hostname'),
        supabase.from('reparti').select('id, nome').order('nome'),
      ]);
      if (pRes.error) throw pRes.error;
      if (prRes.error) throw prRes.error;
      setPool(pRes.data || []);
      setPrestiti(prRes.data || []);
      setDevices(dRes.data || []);
      setReparti(rRes.data || []);
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreatePool = () => {
    setEditItem(null);
    setFormData({ device_id: '', disponibile: true, posizione_magazzino: '', note: '' });
    setShowForm(true);
  };

  const openCreatePrestito = () => {
    setEditItem(null);
    setFormData({
      device_id: '', reparto_origine_id: '', reparto_destinazione_id: '',
      data_restituzione_prevista: '', stato: 'attivo', motivo: '', note: '',
    });
    setShowForm(true);
  };

  const handleSavePool = async () => {
    if (!formData.device_id) { toast.error('Seleziona un dispositivo'); return; }
    setSaving(true);
    try {
      if (editItem) {
        const { error } = await supabase.from('pool_dispositivi').update({ ...formData, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Pool aggiornato');
      } else {
        const { error } = await supabase.from('pool_dispositivi').insert(formData);
        if (error) throw error;
        toast.success('Dispositivo aggiunto al pool');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrestito = async () => {
    if (!formData.device_id || !formData.data_restituzione_prevista) { toast.error('Dispositivo e data restituzione obbligatori'); return; }
    // Validate date: restituzione_prevista must be after today
    if (new Date(formData.data_restituzione_prevista) <= new Date()) {
      toast.error('La data di restituzione prevista deve essere futura');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        reparto_origine_id: formData.reparto_origine_id || null,
        reparto_destinazione_id: formData.reparto_destinazione_id || null,
      };
      if (editItem) {
        const { error } = await supabase.from('prestiti_reparti').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', editItem.id);
        if (error) throw error;
        toast.success('Prestito aggiornato');
      } else {
        const { error } = await supabase.from('prestiti_reparti').insert(payload);
        if (error) throw error;
        toast.success('Prestito registrato');
      }
      setShowForm(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestituisci = async (p: Prestito) => {
    try {
      const { error } = await supabase.from('prestiti_reparti').update({
        stato: 'restituito', data_restituzione_effettiva: new Date().toISOString(), updated_at: new Date().toISOString()
      }).eq('id', p.id);
      if (error) throw error;
      toast.success('Restituzione registrata');
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const handleDelete = async () => {
    const table = activeTab === 'pool' ? 'pool_dispositivi' : 'prestiti_reparti';
    try {
      const { error } = await supabase.from(table).delete().eq('id', deleteConfirm.id);
      if (error) throw error;
      toast.success('Eliminato');
      setDeleteConfirm({ open: false, id: '', name: '' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const getDaysUntil = (dateStr: string) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster richColors position="top-right" />
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
        <div>
          <h1 className="text-2xl font-bold text-white">Pool e Prestiti Dispositivi</h1>
          <p className="text-sm text-muted-color mt-0.5">Riserve, sostituzione temporanea e prestiti tra reparti</p>
        </div>
        {canWrite() && (
          <button onClick={activeTab === 'pool' ? openCreatePool : openCreatePrestito}
            className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold transition-colors">
            <Icon name="PlusIcon" size={18} />
            {activeTab === 'pool' ? 'Aggiungi al Pool' : 'Nuovo Prestito'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-4">
        {(['pool', 'prestiti'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${activeTab === tab ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white hover:bg-surface-elevated'}`}>
            {tab === 'pool' ? `Pool Riserva (${pool.length})` : `Prestiti (${prestiti.filter(p => p.stato === 'attivo').length} attivi)`}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'pool' ? (
          pool.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-color">
              <Icon name="ArchiveBoxIcon" size={48} className="mb-3 opacity-30" />
              <p className="text-lg font-semibold">Pool vuoto</p>
              <p className="text-sm mt-1">Aggiungi dispositivi di riserva al pool</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pool.map(p => (
                <div key={p.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/40 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${p.disponibile ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                        <Icon name="ArchiveBoxIcon" size={20} className={p.disponibile ? 'text-emerald-400' : 'text-amber-400'} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{p.device?.hostname || 'Dispositivo'}</h3>
                        <p className="text-xs text-muted-color">{p.device?.categoria} — {p.device?.modello}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${p.disponibile ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {p.disponibile ? 'Disponibile' : 'In uso'}
                    </span>
                  </div>
                  {p.posizione_magazzino && (
                    <p className="text-sm text-secondary-color mb-2">
                      <span className="text-muted-color">Posizione:</span> {p.posizione_magazzino}
                    </p>
                  )}
                  {p.note && <p className="text-sm text-secondary-color">{p.note}</p>}
                  {canWrite() && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                      <button onClick={() => { setEditItem(p); setFormData({ device_id: p.device_id, disponibile: p.disponibile, posizione_magazzino: p.posizione_magazzino, note: p.note }); setShowForm(true); }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors">
                        <Icon name="PencilIcon" size={14} /> Modifica
                      </button>
                      <button onClick={() => setDeleteConfirm({ open: true, id: p.id, name: p.device?.hostname || 'dispositivo' })}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                        <Icon name="TrashIcon" size={14} /> Rimuovi
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          prestiti.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-color">
              <Icon name="ArrowsRightLeftIcon" size={48} className="mb-3 opacity-30" />
              <p className="text-lg font-semibold">Nessun prestito registrato</p>
            </div>
          ) : (
            <div className="space-y-3">
              {prestiti.map(p => {
                const days = getDaysUntil(p.data_restituzione_prevista);
                return (
                  <div key={p.id} className="bg-surface border border-border rounded-xl p-5 hover:border-sky-500/40 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="ArrowsRightLeftIcon" size={20} className="text-sky-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-white">{p.device?.hostname}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATO_PRESTITO_COLORS[p.stato]}`}>{p.stato}</span>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-secondary-color">
                            {p.reparto_origine?.nome && (
                              <span className="flex items-center gap-1">
                                <Icon name="ArrowRightIcon" size={12} className="text-muted-color" />
                                Da: {p.reparto_origine.nome}
                              </span>
                            )}
                            {p.reparto_destinazione?.nome && (
                              <span className="flex items-center gap-1">
                                <Icon name="ArrowRightIcon" size={12} className="text-muted-color" />
                                A: {p.reparto_destinazione.nome}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-muted-color">
                            <span>Prestito: {new Date(p.data_prestito).toLocaleDateString('it-IT')}</span>
                            <span>Restituzione prevista: {new Date(p.data_restituzione_prevista).toLocaleDateString('it-IT')}</span>
                            {p.stato === 'attivo' && (
                              <span className={`font-semibold ${days < 0 ? 'text-red-400' : days <= 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                {days < 0 ? `Scaduto ${Math.abs(days)}gg fa` : `${days} giorni rimasti`}
                              </span>
                            )}
                          </div>
                          {p.motivo && <p className="text-sm text-secondary-color mt-1"><span className="text-muted-color">Motivo:</span> {p.motivo}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {p.stato === 'attivo' && canWrite() && (
                          <button onClick={() => handleRestituisci(p)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-sm font-semibold transition-colors">
                            <Icon name="CheckIcon" size={14} /> Restituito
                          </button>
                        )}
                        {canWrite() && (
                          <button onClick={() => setDeleteConfirm({ open: true, id: p.id, name: p.device?.hostname || 'prestito' })}
                            className="p-2 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors"><Icon name="TrashIcon" size={14} /></button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-white">
                {editItem ? 'Modifica' : activeTab === 'pool' ? 'Aggiungi al Pool' : 'Nuovo Prestito'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {activeTab === 'pool' ? (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Dispositivo *</label>
                    <select value={formData.device_id || ''} onChange={e => setFormData({ ...formData, device_id: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                      <option value="">Seleziona dispositivo</option>
                      {devices.map(d => <option key={d.id} value={d.id}>{d.hostname} ({d.categoria})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Posizione Magazzino</label>
                    <input value={formData.posizione_magazzino || ''} onChange={e => setFormData({ ...formData, posizione_magazzino: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="es. Magazzino Piano -1, Scaffale A3" />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="disponibile" checked={formData.disponibile ?? true} onChange={e => setFormData({ ...formData, disponibile: e.target.checked })}
                      className="w-4 h-4 rounded border-border text-sky-500" />
                    <label htmlFor="disponibile" className="text-sm font-semibold text-secondary-color">Disponibile</label>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                    <textarea value={formData.note || ''} onChange={e => setFormData({ ...formData, note: e.target.value })} rows={2}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                      placeholder="Note sul dispositivo..." />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Dispositivo *</label>
                    <select value={formData.device_id || ''} onChange={e => setFormData({ ...formData, device_id: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                      <option value="">Seleziona dispositivo</option>
                      {devices.map(d => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Reparto Origine</label>
                      <select value={formData.reparto_origine_id || ''} onChange={e => setFormData({ ...formData, reparto_origine_id: e.target.value || null })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                        <option value="">Nessuno</option>
                        {reparti.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-color mb-1.5">Reparto Destinazione</label>
                      <select value={formData.reparto_destinazione_id || ''} onChange={e => setFormData({ ...formData, reparto_destinazione_id: e.target.value || null })}
                        className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500">
                        <option value="">Nessuno</option>
                        {reparti.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Data Restituzione Prevista *</label>
                    <input type="date" value={formData.data_restituzione_prevista || ''} onChange={e => setFormData({ ...formData, data_restituzione_prevista: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Motivo</label>
                    <input value={formData.motivo || ''} onChange={e => setFormData({ ...formData, motivo: e.target.value })}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                      placeholder="es. Guasto analizzatore principale" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                    <textarea value={formData.note || ''} onChange={e => setFormData({ ...formData, note: e.target.value })} rows={2}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                      placeholder="Note aggiuntive..." />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-border rounded-lg text-secondary-color hover:bg-surface-elevated font-semibold transition-colors">Annulla</button>
              <button onClick={activeTab === 'pool' ? handleSavePool : handleSavePrestito} disabled={saving}
                className="flex-1 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina"
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"?`}
        confirmLabel="Elimina"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
